<!-- =========================================================================================
  File Name: ReferralCheckList.vue
  Description: Referral Checks Page
  ----------------------------------------------------------------------------------------
  Item Name: iTOVi - Admin
========================================================================================== -->

<template>
  <div>
    <div id="main-container">

      <div id="algolia-content-container" class="relative clearfix">
        <!-- Filters -->
        <vx-card class="mb-2">
          <h4>Filters</h4>

          <vs-row>
            <vs-col vs-w="3">
              <label class="text-sm opacity-75">Status</label>
              <div class="dropdown-button-container">
                <vs-dropdown style="width: 100%;">
                  <vs-button class="btn-drop" style="width: 100%;" type="border" icon-after icon="expand_more">{{ filterOptions.status.selected }}</vs-button>
                  <vs-dropdown-menu style="min-width: 120px;">
                    <vs-dropdown-item v-for="statusOption in filterOptions.status.options"
                                      :key="statusOption"
                                      @click="setSelectedStatus(statusOption);">{{ statusOption }}</vs-dropdown-item>
                  </vs-dropdown-menu>
                </vs-dropdown>
              </div>
            </vs-col>
            <vs-col vs-w="3">
              <label class="text-sm opacity-75">Country</label>
              <div class="dropdown-button-container">
                <vs-dropdown style="width: 100%;">
                  <vs-button class="btn-drop" style="width: 100%;" type="border" icon-after icon="expand_more">{{ filterOptions.country.selected.key }}</vs-button>
                  <vs-dropdown-menu style="min-width: 120px;">
                    <vs-dropdown-item v-for="countryOption in filterOptions.country.options"
                                      :key="countryOption.key"
                                      @click="setSelectedCountry(countryOption);">{{ countryOption.key }}</vs-dropdown-item>
                  </vs-dropdown-menu>
                </vs-dropdown>
              </div>
            </vs-col>
            <vs-col vs-w="3"></vs-col>
            <vs-col vs-w="3">
              <vs-button style="width: 100%;" type="border" @click="download()">Download {{ filterOptions.status.selected }} checks <download-icon size="1.5x" class="custom-class"></download-icon></vs-button>
            </vs-col>
          </vs-row>
        </vx-card>

        <vx-card>
          <vs-row class="mb-4">
            <vs-col vs-w="6"></vs-col>
            <vs-col vs-w="6">
              <!-- SEARCH INPUT -->
              <vs-input class="w-full vs-input-shadow-drop vs-input-no-border d-theme-input-dark-bg mb-4"
                        type="text"
                        placeholder="Search..."
                        size="large"
                        v-model="searchFilters.filters.search"
                        v-on:keyup.enter="resetPaginationAndFetch"
                        v-on:keyup.esc="searchFilters.filters.search = ''"
              />
            </vs-col>
          </vs-row>

          <vs-row>
            <vs-col :vs-w="header.width" v-for="(header, index) in (legacy ? legacyHeaders : headers)" :key="index">
              <div @click="changeOrder(header.value)" style="cursor: pointer;">
                <span style="font-weight: bold;">{{ header.text }}</span>
                <arrow-down-icon v-if="order.by === header.value && order.desc" size="1.5x" class="custom-class"></arrow-down-icon>
                <arrow-up-icon v-if="order.by === header.value && !order.desc" size="1.5x" class="custom-class"></arrow-up-icon>
              </div>
            </vs-col>
          </vs-row>

          <vs-divider color="lightgray" />

          <div v-if="legacy">
            <vs-row v-for="check in checks" :key="check.payt_id" @click.native="showLegacyCheckModal(check)" style="cursor: pointer;">
              <vs-col vs-w="2">{{ check.invoice.shipping_first_name }}</vs-col>
              <vs-col vs-w="2">{{ check.invoice.shipping_last_name }}</vs-col>
              <vs-col vs-w="4">{{ check.user.paypal_email }}</vs-col>
              <vs-col vs-w="1">{{ check.invoice.invc_id }}</vs-col>
              <vs-col vs-w="1">{{ check.invoice.creation_date.format("MMMM Do YYYY") }}</vs-col>
              <vs-col vs-w="1">${{ check.amount }}</vs-col>
              <vs-col vs-w="1">
                <span v-if="check.status === 'paper_check_pending'">Pending</span>
                <span v-else-if="check.status === 'paper_check_sent'">Sent</span>
              </vs-col>

              <vs-divider color="lightgray" />
            </vs-row>
          </div>
          <div v-else>
            <vs-row v-for="check in checks" :key="check.id" @click.native="showCheckModal(check)" style="cursor: pointer;">
              <vs-col vs-w="2">{{ check.id }}</vs-col>
              <vs-col vs-w="2">{{ check.first_name }}</vs-col>
              <vs-col vs-w="2">{{ check.last_name }}</vs-col>
              <vs-col vs-w="2">{{ (check.issue_date === null || check.issue_date === "") ? "" : check.issue_date.format("MMMM Do YYYY") }}</vs-col>
              <vs-col vs-w="2">${{ check.amount }}</vs-col>
              <vs-col vs-w="2">{{ check.status }}</vs-col>

              <vs-divider color="lightgray" />
            </vs-row>
          </div>

          <!-- PAGINATION -->
          <vs-pagination
            :total="searchFilters.pagination.pageCount"
            :max="9"
            :value="searchFilters.pagination.page"
            @input="(val) => { searchFilters.pagination.page = val; fetchReferralChecks(); }"
            style="justify-content: flex-end; display: flex; align-items: center; margin-left: 0; width: 100%;"/>
        </vx-card>
      </div>
    </div>

    <vs-prompt
      buttons-hidden
      title="Referral Check Info"
      :active.sync="checkModalVisible">
      <div v-if="legacy" class="modal-lg">
        <vs-row>
          <vs-col vs-w="4">User:</vs-col>
          <vs-col vs-w="8">{{ modalInvoice.shipping_first_name }} {{ modalInvoice.shipping_last_name }} (ID: {{ modalInvoice.user_id }})</vs-col>
        </vs-row>
        <vs-divider color="lightgray" />
        <vs-row>
          <vs-col vs-w="4">Invoice ID:</vs-col>
          <vs-col vs-w="8">{{ modalInvoice.invc_id }}</vs-col>
        </vs-row>
        <vs-divider color="lightgray" />
        <vs-row>
          <vs-col vs-w="4">Payment ID:</vs-col>
          <vs-col vs-w="8">{{ modalPaymentTransaction.payt_id }}</vs-col>
        </vs-row>
        <vs-divider color="lightgray" />
        <vs-row>
          <vs-col vs-w="4">Check amount:</vs-col>
          <vs-col vs-w="8">${{ modalPaymentTransaction.amount }}</vs-col>
        </vs-row>
        <vs-divider color="lightgray" />
        <vs-row>
          <vs-col vs-w="4" style="align-self: center;">Payment Method:</vs-col>
          <vs-col vs-w="8">
            <vs-radio required v-model="modalPaymentTransaction.payment_detail_json.payment_method" vs-name="payment_method" vs-value="check">Check</vs-radio>
            <vs-radio class="ml-5" v-model="modalPaymentTransaction.payment_detail_json.payment_method" vs-name="payment_method" vs-value="paypal">Paypal</vs-radio>
            <span v-if="errors.payment_method.error">
              <div class="con-text-validation span-text-validation-danger vs-input--text-validation-span v-enter-to" style="height: 19px;">
                <span class="span-text-validation">{{ errors.payment_method.description }}</span>
              </div>
            </span>
          </vs-col>
        </vs-row>
        <vs-divider color="lightgray" />
        <div v-if="modalPaymentTransaction.payment_detail_json.payment_method && modalPaymentTransaction.payment_detail_json.payment_method === 'paypal'">
          <vs-row>
            <vs-col vs-w="4">PayPal email:</vs-col>
            <vs-col vs-w="8">
              <vs-input class="w-full" v-model="modalPaymentTransaction.user.paypal_email" />
              <span v-if="errors.paypal_email.error">
              <div class="con-text-validation span-text-validation-danger vs-input--text-validation-span v-enter-to" style="height: 19px;">
                <span class="span-text-validation">{{ errors.paypal_email.description }}</span>
              </div>
            </span>
            </vs-col>
          </vs-row>
          <vs-divider color="lightgray" />
        </div>
        <div v-else>
          <vs-row>
            <vs-col vs-w="4" style="align-self: center;">Check number:</vs-col>
            <vs-col vs-w="8">
              <vs-input
                :danger="errors.checkNumber.error"
                :danger-text="errors.checkNumber.description"
                v-model="modalPaymentTransaction.payment_detail_json.check_number"/>
            </vs-col>
          </vs-row>
          <vs-divider color="lightgray" />
        </div>
        <vs-row>
          <vs-col vs-w="4" style="align-self: center;">Issue Date:</vs-col>
          <vs-col vs-w="5">
            <datepicker
              name="checkDate"
              v-model="modalPaymentTransaction.payment_detail_json.check_date"/>
            <span v-if="errors.checkDate.error">
              <div class="con-text-validation span-text-validation-danger vs-input--text-validation-span v-enter-to" style="height: 19px;">
                <span class="span-text-validation">{{ errors.checkDate.description }}</span>
              </div>
            </span>
          </vs-col>
          <vs-col vs-w="3">
            <vs-button class="ml-auto" style="width: 100%; padding: 8px 26px;" color="primary" type="border" @click="setCheckDateToToday(true)">Today</vs-button>
          </vs-col>
        </vs-row>
        <vs-divider color="lightgray" />
        <vs-row>
          <vs-col vs-w="4">Address:</vs-col>
          <vs-col vs-w="8">
            {{ modalInvoice.shipping_first_name }} {{ modalInvoice.shipping_last_name }}<br>
            {{ modalInvoice.subdata_json.shipping.address }}<br>
            <span v-if="modalInvoice.subdata_json.shipping.address_2 !== null && modalInvoice.subdata_json.shipping.address_2 !== ''">{{ modalInvoice.subdata_json.shipping.address_2 }}<br></span>
            {{ modalInvoice.subdata_json.shipping.city }} {{ modalInvoice.subdata_json.shipping.state }}, {{ modalInvoice.subdata_json.shipping.postal_code }}<br>
            {{ modalInvoice.subdata_json.shipping.country }}<br>
          </vs-col>
        </vs-row>

        <vs-button class="ml-auto mt-5 pt-2" style="width: 100%;" color="primary" type="border" @click="saveLegacyCheckInfo()">Save and Close</vs-button>
        <vs-alert :active.sync="showAlert.error" color="danger" icon-pack="feather" close-icon="alert-triangle">{{ showAlert.description }}</vs-alert>

        <vs-divider color="lightgray" />
        <vs-row>
          <vs-col vs-w="12">Check Referrals:</vs-col>
          <vs-col vs-w="12">
            <vs-row>
              <vs-col vs-w="1">#</vs-col>
              <vs-col vs-w="6">Name</vs-col>
              <vs-col vs-w="5">Signup Date</vs-col>
            </vs-row>
            <hr>
            <vs-row v-for="(lineItem, index) in modalInvoice.subdata_json.lineitems" :key="index">
              <vs-col vs-w="1">{{ index + 1 }}</vs-col>
              <vs-col vs-w="6">{{ lineItem.referral_name }}</vs-col>
              <vs-col vs-w="5">{{ lineItem.referral_signup_date.format("MMMM Do YYYY") }}</vs-col>
            </vs-row>
          </vs-col>
        </vs-row>

        <div v-if="modalPendingReferrals.length">
          <vs-divider color="lightgray" />
          <vs-row>
            <vs-col vs-w="12">Pending Referrals:</vs-col>
            <vs-col vs-w="12">
              <vs-row>
                <vs-col vs-w="1">#</vs-col>
                <vs-col vs-w="6">Name</vs-col>
                <vs-col vs-w="5">Signup Date</vs-col>
              </vs-row>
              <hr>
              <vs-row v-for="(referral, index) in modalPendingReferrals" :key="index">
                <vs-col vs-w="1">{{ index + 1 }}</vs-col>
                <vs-col vs-w="6">{{ referral.name }}</vs-col>
                <vs-col vs-w="5">{{ referral.signup_date.format("MMMM Do YYYY") }}</vs-col>
              </vs-row>
            </vs-col>
          </vs-row>
        </div>
      </div>
      <div v-else class="modal-lg">
        <vs-row>
          <vs-col vs-w="4">User:</vs-col>
          <vs-col vs-w="8">{{ modalCheck.first_name }} {{ modalCheck.last_name }} (ID: {{ modalCheck.user_id }})</vs-col>
        </vs-row>
        <vs-divider color="lightgray" />
        <vs-row>
          <vs-col vs-w="4">Check amount:</vs-col>
          <vs-col vs-w="8">${{ modalCheck.amount }}</vs-col>
        </vs-row>
        <vs-divider color="lightgray" />
        <vs-row>
          <vs-col vs-w="4" style="align-self: center;">Check number:</vs-col>
          <vs-col vs-w="8">
            <vs-input
              :danger="errors.checkNumber.error"
              :danger-text="errors.checkNumber.description"
              v-model="modalCheck.check_number"/>
          </vs-col>
        </vs-row>
        <vs-divider color="lightgray" />
        <vs-row>
          <vs-col vs-w="4" style="align-self: center;">Check number:</vs-col>
          <vs-col vs-w="8">
            <vs-input
              :danger="errors.checkNumber.error"
              :danger-text="errors.checkNumber.description"
              v-model="modalCheck.check_number"/>
          </vs-col>
        </vs-row>
        <vs-divider color="lightgray" />
        <vs-row>
          <vs-col vs-w="4" style="align-self: center;">Issue Date:</vs-col>
          <vs-col vs-w="5">
            <datepicker
              name="checkDate"
              v-model="modalCheck.issue_date"/>
            <span v-if="errors.checkDate.error">
              <div class="con-text-validation span-text-validation-danger vs-input--text-validation-span v-enter-to" style="height: 19px;">
                <span class="span-text-validation">{{ errors.checkDate.description }}</span>
              </div>
            </span>
          </vs-col>
          <vs-col vs-w="3">
            <vs-button class="ml-auto" style="width: 100%; padding: 8px 26px;" color="primary" type="border" @click="setCheckDateToToday(false)">Today</vs-button>
          </vs-col>
        </vs-row>
        <vs-divider color="lightgray" />
        <vs-row>
          <vs-col vs-w="4">PayPal email:</vs-col>
          <vs-input :v-model="modalCheck.user.paypal_email" vs-w="8">{{ modalCheck.user.paypal_email }}</vs-input>
        </vs-row>
        <vs-divider color="lightgray" />
        <vs-row>
          <vs-col vs-w="4">Address:</vs-col>
          <vs-col vs-w="8">
            {{ modalCheck.first_name }} {{ modalCheck.last_name }}<br>
            {{ modalCheck.address }}<br>
            <span v-if="modalCheck.address_2 !== null && modalCheck.address_2 !== ''">{{ modalCheck.address_2 }}<br></span>
            {{ modalCheck.city }} {{ modalCheck.state }}, {{ modalCheck.postal_code }}<br>
            {{ modalCheck.country }}<br>
          </vs-col>
        </vs-row>

        <vs-button class="ml-auto mt-5 pt-2" style="width: 100%;" color="primary" type="border" @click="saveCheckInfo()">Save and Close</vs-button>
        <vs-alert :active.sync="showAlert.error" color="danger" icon-pack="feather" close-icon="alert-triangle">{{ showAlert.description }}</vs-alert>

        <vs-divider color="lightgray" />
        <vs-row>
          <vs-col vs-w="12">Check Referrals:</vs-col>
          <vs-col vs-w="12">
            <vs-row>
              <vs-col vs-w="1">#</vs-col>
              <vs-col vs-w="6">Name</vs-col>
              <vs-col vs-w="5">Signup Date</vs-col>
            </vs-row>
            <hr>
            <vs-row v-for="(referral, index) in modalCheck.referrals" :key="index">
              <vs-col vs-w="1">{{ index + 1 }}</vs-col>
              <vs-col vs-w="6">{{ referral.name }}</vs-col>
              <vs-col vs-w="5">{{ referral.signup_date }}</vs-col>
            </vs-row>
          </vs-col>
        </vs-row>
      </div>
    </vs-prompt>

  </div>
</template>

<script>
  import api from '@/http/api';
  import Datepicker from 'vuejs-datepicker';
  import models from '@/models';
  import moment from 'moment';
  import { ArrowDownIcon, ArrowUpIcon, DownloadIcon } from 'vue-feather-icons';
  import { ExportToCsv } from 'export-to-csv';

  export default {
    components: {
      Datepicker,
      ArrowDownIcon,
      ArrowUpIcon,
      DownloadIcon
    },
    data() {
      return {
        models: models,
        isSearchStalled: false,
        currentRefinement: true,
        searchFilters: {
          filters: {
            search: '',
            transactionMethod: null,
            status: ["paper_check_pending", "paper_check_sent"],
          },
          pagination: {
            total: 0,
            count: 10,
            page: 1,
            pageCount: 1,
          },
        },
        filterOptions: {
          status: {
            selected: "All Legacy",
            options: [
              //"All",
              //"waiting",
              //"pending",
              //"sent",
              "All Legacy",
              "paper_check_pending",
              "paper_check_sent"
            ]
          },
          country: {
            selected: { key: "All", value: "all" },
            options: [
              { key: "All", value: "all" },
              { key: "United States", value: "US" },
            ]
          }
        },
        checks: [],
        legacyHeaders: [
          { text: 'First Name',   value: 'shipping_first_name', width: 2 },
          { text: 'Last Name',    value: 'shipping_last_name',  width: 2 },
          { text: 'Paypal email', value: 'paypal_email',        width: 4 },
          { text: 'Invoice #',    value: 'invc_id',             width: 1 },
          { text: 'Invoice Date', value: 'creation_date',       width: 1 },
          { text: 'Amount',       value: 'amount',              width: 1 },
          { text: 'Status',       value: 'status',              width: 1 }
        ],
        headers: [
          { text: 'Check ID',   value: 'id',          width: 2 },
          { text: 'First Name', value: 'first_name',  width: 2 },
          { text: 'Last Name',  value: 'last_name',   width: 2 },
          { text: 'Issue Date', value: 'issue_date',  width: 2 },
          { text: 'Amount',     value: 'amount',      width: 2 },
          { text: 'Status',     value: 'status',      width: 2 }
        ],
        checkModalVisible: false,
        modalInvoice: {
          subdata_json: {
            shipping: {},
            lineitems: []
          }
        },
        modalPaymentTransaction: {
          payment_detail_json: {
            check_number: '',
            check_date: ''
          },
          user: {
            paypal_email: '',
          },
          original_payment_method: ''
        },
        modalCheck: {
          id: 0,
          check_number: '',
          issue_date: '',
          user: {
            paypal_email: '',
          },
        },
        modalPendingReferrals: [],
        errors: {
          checkNumber: {
            error: false,
            description: "Check number is required"
          },
          checkDate: {
            error: false,
            description: "Issue Date is required"
          },
          payment_method: {
            error: false,
            description: "Payment method is required"
          },
          paypal_email: {
            error: false,
            description: "Paypal email is required"
          }
        },
        showAlert: {
          error: false,
          description: ""
        },
        lastLoad: moment("04/01/2020 00:00:00"),
        //When we switch to the new system change bool to false
        legacy: true,
        order: {
          by: "id",
          desc: true
        }
      }
    },
    mounted() {
      this.fetchReferralChecks();
    },
    computed: {},
    watch: {},
    methods: {
      changeOrder(by) {
        if (this.order.by === by) {
          this.order.desc = !this.order.desc;
        }
        else {
          this.order.by = by;
          this.order.desc = true;
        }

        this.fetchReferralChecks();
      },
      async showCheckModal(check) {
        this.checkModalVisible = true;
        this.modalCheck = JSON.parse(JSON.stringify(check));

        if (this.modalCheck.issue_date === null || this.modalCheck.issue_date === "") {
          this.modalCheck.issue_date = "";
        }
        else {
          let date = new Date(this.modalCheck.issue_date);
          date.setMinutes(date.getMinutes() + date.getTimezoneOffset())
          this.modalCheck.issue_date = date;
        }

        for (const i in Object.keys(this.modalCheck.referrals)) {
          this.modalCheck.referrals[i].signup_date = moment(this.modalCheck.referrals[i].signup_date).format("MMMM Do YYYY");
        }
      },
      async showLegacyCheckModal(check) {
        this.checkModalVisible = true;
        this.modalInvoice = check.invoice;

        check.invoice.subdata_json.lineitems.forEach((lineItem) => {
          lineItem.referral_signup_date = moment(lineItem.referral_signup_date);
        })

        this.modalPaymentTransaction = check;
        this.modalPaymentTransaction.original_payment_method = check.user.paypal_email;

        if (this.modalPaymentTransaction.payment_detail_json === null) {
          this.modalPaymentTransaction.payment_detail_json = {
            check_number: "",
            check_date: "",
            payment_method: ""
          }
        }
        else {
          let date = new Date(this.modalPaymentTransaction.payment_detail_json.check_date);

          if (isNaN(date.getTime())) {
            this.modalPaymentTransaction.payment_detail_json.check_date = "";
          }
          else {
            date.setMinutes(date.getMinutes() + date.getTimezoneOffset())
            this.modalPaymentTransaction.payment_detail_json.check_date = date;
          }
        }

        const referrals = await api.getUserReferrals(check.user_id);
        this.modalPendingReferrals = [];
        referrals.forEach((referral) => {
          if (referral.pending) {
            const signup_date = moment(referral.user.membership_active_date);
            this.modalPendingReferrals.push({
              name: `${referral.user.first_name} ${referral.user.last_name}`,
              signup_date: signup_date
            })
          }
        });
      },
      calculatePageCount() {
        let total = this.searchFilters.pagination.total;
        let perPage = this.searchFilters.pagination.count;
        let pages = Math.floor(total / perPage);
        let plus1 = total % perPage !== 0;
        if (plus1) pages++;

        this.searchFilters.pagination.pageCount = pages;
      },
      async fetchReferralChecks() {
        let msSinceLastFetch = this.lastLoad.diff(moment());
        if (Math.abs(msSinceLastFetch) < 1000) return;

        this.$vs.loading();

        this.isSearchStalled = true;

        let referralChecksObj = await api.fetchReferralChecks(this.buildSearchFilters(), this.legacy);
        this.checks = referralChecksObj.checks;

        if (!this.legacy) {
          this.checks.forEach((check) => {
            if (check.issue_date !== null) {
              check.issue_date = moment(check.issue_date);
            }
          });
        }

        this.searchFilters.pagination.total = referralChecksObj.pagination.total;
        this.searchFilters.pagination.page = referralChecksObj.pagination.page;
        this.calculatePageCount();

        this.isSearchStalled = false;

        this.$vs.loading.close();
        this.lastLoad = moment();
      },
      setSelectedStatus(newStatus) {
        this.checks = [];
        let tempLegacy = this.legacy;

        if (newStatus === "All Legacy") {
          this.legacy = true;
          this.searchFilters.filters.status = ["paper_check_pending", "paper_check_sent"];
        }
        else if (["paper_check_pending", "paper_check_sent"].includes(newStatus)) {
          this.legacy = true;
          this.searchFilters.filters.status = newStatus;
        }
        else if (newStatus === "All") {
          this.legacy = false;
          this.searchFilters.filters.status = "";
        }
        else {
          this.legacy = false;
          this.searchFilters.filters.status = newStatus;
        }

        if (tempLegacy !== this.legacy) {
          this.order.by = this.legacy ? "invc_id" : "id";
        }

        this.filterOptions.status.selected = newStatus;

        this.resetPaginationAndFetch();
      },
      setSelectedCountry(countryOption) {
        this.searchFilters.filters.country = countryOption.value;
        this.filterOptions.country.selected = countryOption;
        this.resetPaginationAndFetch();
      },
      buildSearchFilters() {
        let descAsc = this.order.desc ? "DESC" : "ASC";

        if (this.legacy) {
         // this.searchFilters.filters.transactionMethod = "paper_check";
          this.searchFilters.referralChecks = true;
          this.searchFilters.include = ["invoice", "user"];
          this.order.by = this.order.by === "id" ? "invc_id" : this.order.by;

          if (["shipping_first_name", "shipping_last_name", "creation_source", "creation_date"].includes(this.order.by)) {
            this.searchFilters.order = [["invoice", this.order.by, descAsc]];
          }
          else if ("paypal_email" === this.order.by) {
            this.searchFilters.order = [["user", this.order.by, descAsc]];
          }
          else {
            this.searchFilters.order = [[this.order.by, descAsc]];
          }
        }
        else {
          this.searchFilters.filters.transactionMethod = null;
          this.searchFilters.include = ["referrals", "user"];
          this.order.by = this.order.by === "invc_id" ? "id" : this.order.by;
          this.searchFilters.order = [[this.order.by, descAsc]];
        }

        return this.searchFilters;
      },
      setCheckDateToToday(legacy) {
        if (legacy) {
          this.modalPaymentTransaction.payment_detail_json.check_date = new Date();
        }
        else {
          this.modalCheck.issue_date = new Date();
        }
      },
      async saveLegacyCheckInfo() {
        const { check_number, check_date, payment_method } = this.modalPaymentTransaction.payment_detail_json;
        const { paypal_email } = this.modalPaymentTransaction.user;
        this.resetAlert();
        console.log('check number: ',check_number)
        this.errors.checkNumber.error = check_number === "";
        this.errors.checkDate.error = check_date === "";
        this.errors.paypal_email.error = payment_method === 'paypal' && !paypal_email
        this.errors.payment_method.error = !payment_method;

        if (payment_method === 'check') {
          if (this.errors.checkNumber.error || this.errors.checkDate.error || this.errors.payment_method.error) {
            return;
          }
        }
        else {
          if (this.errors.paypal_email.error || this.errors.checkDate.error || this.errors.payment_method.error) {
            return;
          }
        }



        let body = {
          status: "paper_check_sent",
          check_date: moment(check_date).format("YYYY-MM-DD"),
          invoice: { status: "complete" },
          payment_method: payment_method
        };

        if (body.payment_method === 'paypal') {
          body.paypal_email = paypal_email;
        }
        else {
          body.check_number = check_number;
        }

        if (body.payment_method === 'paypal' && this.modalPaymentTransaction.original_payment_method !== this.modalPaymentTransaction.user.paypal_email) {
          let userResponse = await api.updateUser(this.modalPaymentTransaction.user_id, {paypal_email: this.modalPaymentTransaction.user.paypal_email});
          if (userResponse === null) {
            return this.showAlert = {
              error: true,
              description: 'Unable to save users paypal email'
            };
          }
        }

        let response = await api.patchPaymentTransaction(this.modalPaymentTransaction.payt_id, body);

        if (response === null) {
          return this.showAlert = {
            error: true,
            description: 'Unable to save referral check'
          };
        }

        this.modalPaymentTransaction.status = response.status;
        this.checkModalVisible = false;
        await this.fetchReferralChecks();
      },
      async saveCheckInfo() {
        this.resetAlert()
        this.errors.checkNumber.error = this.modalCheck.check_number === "";
        this.errors.checkDate.error = this.modalCheck.issue_date === "";

        if (this.errors.checkNumber.error || this.errors.checkDate.error) {
          return;
        }

        let body = {
          status: "sent",
          check_number: this.modalCheck.check_number,
          issue_date: moment(this.modalCheck.issue_date).format("YYYY-MM-DD HH:mm:ss"),
        };

        let response = await api.patchReferralCheck(this.modalCheck.id, body);

        if (response === null) {
          return this.showAlert = {
            error: true,
            description: 'Unable to save referral check'
          }
        }

        for (let check of this.checks) {
          if (check.id === this.modalCheck.id) {
            check.status = response.status;
            check.issue_date = moment(body.issue_date);
          }
        }
        this.checkModalVisible = false;
      },
      async download() {
        this.$vs.loading();
        const originalCount = this.searchFilters.pagination.count;
        const rowLimit = 1000000000;
        let options = this.buildSearchFilters();
        options.include = [];
        options.pagination.count = rowLimit;
        options.pagination.page = 1;
        let checks = await api.fetchReferralChecks(options, this.legacy, true);

        const fileName = moment().format("MMMM Do YYYY") + ` ${this.filterOptions.status.selected} checks`;
        const csvExporter = new ExportToCsv({
          filename: fileName,
          showLabels: true,
          showTitle: true,
          title: fileName,
          useKeysAsHeaders: true,
        });
        csvExporter.generateCsv(checks);

        this.searchFilters.pagination.count = originalCount;
        this.$vs.loading.close();
      },
      async resetPaginationAndFetch() {
        this.searchFilters.pagination = {
            total: 0,
            count: 10,
            page: 1,
            pageCount: 1,
        };
        await this.fetchReferralChecks();
      },
      resetAlert() {
        this.showAlert = {
          error: false,
          description: ''
        }
      }
    },
    created() {}
  }

</script>


<style lang="scss">
  #main-container {
    .algolia-header {
      .algolia-filters-label {
        width: calc(260px + 2.4rem);
      }
    }

    #algolia-content-container {

      .vs-sidebar {
        position: relative;
        float: left;
      }
    }

    .algolia-search-input-right-aligned-icon {
      padding: 1rem 1.5rem;
    }

    .algolia-price-slider {
      min-width: unset;
    }

    .item-view-primary-action-btn {
      color: #2c2c2c !important;
      background-color: #f6f6f6;
      min-width: 50%;
    }

    .item-view-secondary-action-btn {
      min-width: 50%;
    }
  }

  .theme-dark {
    #main-container {
      #algolia-content-container {
        .vs-sidebar {
          background-color: #10163a;
        }
      }
    }
  }

  .con-vs-dialog {
    .vs-dialog {
      max-width: 600px;
      max-height: 99%;
      overflow-y: scroll;

      .vs-dialog-header {
        .vs-dialog-cancel {
          margin: 8px;
        }
      }
    }
  }

  @media (min-width: 992px) {
    .vs-sidebar-rounded {
      .vs-sidebar {
        border-radius: .5rem;
      }

      .vs-sidebar--items {
        border-radius: .5rem;
      }
    }
  }

  @media (max-width: 992px) {
    #algolia-content-container {
      .vs-sidebar {
        position: absolute !important;
        float: none !important;
      }
    }
  }

</style>

